import type {
  BatteryInput,
  LoadInput,
  EfficiencyInput,
  SafetyInput,
  SensitivityPoint,
  SensitivityResult,
  Scenario,
  ScenarioComparison,
  ScenarioResultEntry,
  PassFailResult,
  LoadEntry,
} from '@brt/shared-types'
import { calculateRuntime } from './runtimeEngine.js'
import type { RuntimeResult } from '@brt/shared-types'

function roundTo2(n: number): number {
  return Math.round(n * 100) / 100
}

export type SensitivityVariable = 'temperature' | 'load' | 'efficiency' | 'dod' | 'soh'

function applyVariableOverride(
  battery: BatteryInput,
  load: LoadInput,
  efficiency: EfficiencyInput,
  variable: SensitivityVariable,
  value: number,
): { battery: BatteryInput; load: LoadInput; efficiency: EfficiencyInput } {
  const b = { ...battery }
  let l = { ...load, loads: load.loads.map(e => ({ ...e })) }
  let e = { ...efficiency }

  switch (variable) {
    case 'temperature':
      b.temperatureC = value
      break
    case 'load': {
      const enabledLoads = l.loads.filter(ld => ld.enabled)
      if (enabledLoads.length > 0 && enabledLoads[0]) {
        l = { ...l, loads: l.loads.map(ld => ld.id === enabledLoads[0]!.id ? { ...ld, loadValue: value } : ld) }
      }
      break
    }
    case 'efficiency':
      e = { ...e, efficiencyPercent: value }
      break
    case 'dod':
      b.dod = value
      break
    case 'soh':
      b.stateOfHealth = value
      break
  }

  return { battery: b, load: l, efficiency: e }
}

export function runSensitivityAnalysis(
  battery: BatteryInput,
  load: LoadInput,
  efficiency: EfficiencyInput,
  safety: SafetyInput,
  variable: SensitivityVariable,
  min: number,
  max: number,
  steps: number = 20,
): SensitivityResult {
  const baselineResult = calculateRuntime(battery, load, efficiency, safety)
  const baselineRuntime = baselineResult.runtimeHours ?? 0

  const dataPoints: SensitivityPoint[] = []
  const stepSize = (max - min) / steps

  for (let i = 0; i <= steps; i++) {
    const value = roundTo2(min + stepSize * i)
    const { battery: b, load: l, efficiency: e } = applyVariableOverride(
      battery, load, efficiency, variable, value,
    )
    const result = calculateRuntime(b, l, e, safety)
    const runtime = result.runtimeHours ?? 0
    const delta = roundTo2(runtime - baselineRuntime)

    dataPoints.push({
      inputValue: value,
      runtimeHours: roundTo2(runtime),
      deltaFromBaseline: delta,
      label: `${variable}=${value}`,
    })
  }

  const insightText = buildInsightText(variable, dataPoints, baselineRuntime)

  return {
    variable,
    baselineRuntimeHours: roundTo2(baselineRuntime),
    dataPoints,
    insightText,
  }
}

function buildInsightText(
  variable: SensitivityVariable,
  points: SensitivityPoint[],
  baseline: number,
): string {
  if (points.length < 2) return `Insufficient data for ${variable} sensitivity analysis.`

  const minRuntime = Math.min(...points.map(p => p.runtimeHours))
  const maxRuntime = Math.max(...points.map(p => p.runtimeHours))
  const range = maxRuntime - minRuntime

  const variableLabels: Record<SensitivityVariable, string> = {
    temperature: 'Temperature',
    load: 'Load current',
    efficiency: 'Efficiency',
    dod: 'Depth of discharge',
    soh: 'State of health',
  }

  const label = variableLabels[variable]

  if (range < 0.01) {
    return `${label} has minimal impact on runtime (variation < 0.01h). Runtime is dominated by other factors.`
  }

  const pctVariation = baseline > 0 ? (range / baseline) * 100 : 0
  const direction = variable === 'load'
    ? 'inversely proportional'
    : variable === 'temperature' || variable === 'efficiency' || variable === 'soh' || variable === 'dod'
      ? 'positively correlated (increasing values increase runtime)'
      : 'correlated'

  return `${label} sensitivity: runtime varies ${roundTo2(pctVariation)}% across the tested range (${roundTo2(minRuntime)}h – ${roundTo2(maxRuntime)}h). Relationship is ${direction}.`
}

export function compareScenarios(
  battery: BatteryInput,
  load: LoadInput,
  efficiency: EfficiencyInput,
  safety: SafetyInput,
  scenarios: Scenario[],
): ScenarioComparison {
  const baselineResult = calculateRuntime(battery, load, efficiency, safety)
  const baselineRuntime = baselineResult.runtimeHours

  const entries: ScenarioResultEntry[] = scenarios.map(scenario => {
    const mergedBattery: BatteryInput = {
      ...battery,
      ...(scenario.batteryOverrides as Partial<BatteryInput>),
    }

    const mergedLoad: LoadInput = {
      ...load,
      loads: load.loads.map((entry: LoadEntry) => ({
        ...entry,
        ...(scenario.loadOverrides?.loadValue != null ? { loadValue: scenario.loadOverrides.loadValue } : {}),
        ...(scenario.loadOverrides?.loadUnit != null ? { loadUnit: scenario.loadOverrides.loadUnit } : {}),
      })),
    }

    const mergedEfficiency: EfficiencyInput = {
      ...efficiency,
      ...(scenario.efficiencyOverrides ?? {}),
    }

    const result = calculateRuntime(mergedBattery, mergedLoad, mergedEfficiency, safety)

    return {
      scenarioId: scenario.id,
      scenarioName: scenario.name,
      runtimeHours: result.runtimeHours,
      error: result.error,
      effectiveCapacityAh: result.effectiveCapacityAh,
      totalLoadA: result.effectiveLoadA,
      cRate: result.cRate,
      deltaFromBaselineHours: baselineRuntime != null && result.runtimeHours != null
        ? roundTo2(result.runtimeHours - baselineRuntime)
        : null,
    }
  })

  return {
    baselineRuntimeHours: baselineRuntime,
    scenarios: entries,
  }
}

export function checkPassFail(
  result: RuntimeResult,
  targetRuntimeHours: number,
): PassFailResult {
  const runtime = result.runtimeHours

  if (runtime == null) {
    return {
      status: 'fail',
      runtimeHours: null,
      targetRuntimeHours,
      marginHours: null,
      marginPercent: null,
      recommendation: 'Cannot determine pass/fail — calculation returned no result.',
    }
  }

  const marginHours = roundTo2(runtime - targetRuntimeHours)
  const marginPercent = roundTo2((marginHours / targetRuntimeHours) * 100)

  let status: PassFailResult['status']
  let recommendation: string

  if (marginPercent > 10) {
    status = 'pass'
    recommendation = `Runtime exceeds target by ${roundTo2(marginPercent)}% (${Math.abs(marginHours).toFixed(1)}h margin). Comfortable margin.`
  } else if (marginPercent >= 0) {
    status = 'marginal'
    recommendation = `Runtime meets target with only ${roundTo2(marginPercent)}% margin (${Math.abs(marginHours).toFixed(1)}h). Consider upsizing for reliability.`
  } else if (marginPercent > -10) {
    status = 'marginal'
    recommendation = `Runtime falls short of target by ${Math.abs(roundTo2(marginPercent))}% (${Math.abs(marginHours).toFixed(1)}h). Upsizing recommended.`
  } else {
    status = 'fail'
    recommendation = `Runtime falls significantly short of target (${Math.abs(roundTo2(marginPercent))}% deficit, ${Math.abs(marginHours).toFixed(1)}h). Battery capacity must be increased.`
  }

  return {
    status,
    runtimeHours: runtime,
    targetRuntimeHours,
    marginHours,
    marginPercent,
    recommendation,
  }
}

export interface PassFailRangeSummary {
  passingCount: number
  totalCount: number
  passingFraction: number
  minPassingValue: number | null
  maxPassingValue: number | null
}

export function passFailAcrossRange(
  result: SensitivityResult,
  targetHours: number,
): PassFailRangeSummary {
  const points = result.dataPoints
  const totalCount = points.length
  if (totalCount === 0) {
    return {
      passingCount: 0,
      totalCount: 0,
      passingFraction: 0,
      minPassingValue: null,
      maxPassingValue: null,
    }
  }

  let passingCount = 0
  let minPassingValue: number | null = null
  let maxPassingValue: number | null = null

  for (const point of points) {
    if (point.runtimeHours >= targetHours) {
      passingCount++
      if (minPassingValue === null || point.inputValue < minPassingValue) {
        minPassingValue = point.inputValue
      }
      if (maxPassingValue === null || point.inputValue > maxPassingValue) {
        maxPassingValue = point.inputValue
      }
    }
  }

  return {
    passingCount,
    totalCount,
    passingFraction: passingCount / totalCount,
    minPassingValue,
    maxPassingValue,
  }
}
